import { MX_PLATE_STATE_ID } from '@m/constants/Regions';

export const usaStateNameMap: { [abbr: string]: string } = {
  AL: 'Alabama',
  AK: 'Alaska',
  AS: 'American Samoa',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'Washington D.C.',
  FM: 'Federated States Of Micronesia',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MH: 'Marshall Islands',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  MP: 'Northern Mariana Islands',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PW: 'Palau',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VI: 'Virgin Islands',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export const cadProvinceNameMap: { [abbr: string]: string } = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NT: 'Northwest Territories',
  NS: 'Nova Scotia',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

// https://metropolisio.atlassian.net/browse/EN-5592
export const usaStateEquivalentToNameMap: { [name: string]: string } = {
  D: 'Diplomat',
  G: 'Government',
  ML: 'Military',
};

// this can be optimized by putting these data in backend
// so the API response comes back with abbrev and full names
export const abbreviationToNameMap: { [abbr: string]: string } = {
  USA: 'USA',
  ...usaStateNameMap,
  OTHER: 'Other',
  CAN: 'CAN',
  ...cadProvinceNameMap,
  ...usaStateEquivalentToNameMap,
  MX: 'Mexico',
};

// creates label given a state abbreviation
// label is usually `Full name (ABBR)` e.g. California (CA)
// exceptions for non USA states and CAD provinces / territories
export function plateAbbreviationToLabel(abbr: string) {
  if (usaStateEquivalentToNameMap[abbr]) {
    return usaStateEquivalentToNameMap[abbr];
  }

  if (abbr === 'MX') {
    return 'Mexico (All States)';
  }

  const usaOrCad = cadProvinceNameMap[abbr] || usaStateNameMap[abbr];
  if (usaOrCad) {
    return `${usaOrCad} (${abbr})`;
  }

  // fallback
  return abbr;
}

export const statesAndProvinces = [
  { id: 1, name: 'AL', fullName: 'Alabama' },
  { id: 2, name: 'AK', fullName: 'Alaska' },
  { id: 3, name: 'AZ', fullName: 'Arizona' },
  { id: 4, name: 'AR', fullName: 'Arkansas' },
  { id: 5, name: 'CA', fullName: 'California' },
  { id: 6, name: 'CO', fullName: 'Colorado' },
  { id: 7, name: 'CT', fullName: 'Connecticut' },
  { id: 8, name: 'DE', fullName: 'Delaware' },
  { id: 9, name: 'FL', fullName: 'Florida' },
  { id: 10, name: 'GA', fullName: 'Georgia' },
  { id: 11, name: 'HI', fullName: 'Hawaii' },
  { id: 12, name: 'ID', fullName: 'Idaho' },
  { id: 13, name: 'IL', fullName: 'Illinois' },
  { id: 14, name: 'IN', fullName: 'Indiana' },
  { id: 15, name: 'IA', fullName: 'Iowa' },
  { id: 16, name: 'KS', fullName: 'Kansas' },
  { id: 17, name: 'KY', fullName: 'Kentucky' },
  { id: 18, name: 'LA', fullName: 'Louisiana' },
  { id: 19, name: 'ME', fullName: 'Maine' },
  { id: 20, name: 'MD', fullName: 'Maryland' },
  { id: 21, name: 'MA', fullName: 'Massachusetts' },
  { id: 22, name: 'MI', fullName: 'Michigan' },
  { id: 23, name: 'MN', fullName: 'Minnesota' },
  { id: 24, name: 'MS', fullName: 'Mississippi' },
  { id: 25, name: 'MO', fullName: 'Missouri' },
  { id: 26, name: 'MT', fullName: 'Montana' },
  { id: 27, name: 'NE', fullName: 'Nebraska' },
  { id: 28, name: 'NV', fullName: 'Nevada' },
  { id: 29, name: 'NH', fullName: 'New Hampshire' },
  { id: 30, name: 'NJ', fullName: 'New Jersey' },
  { id: 31, name: 'NM', fullName: 'New Mexico' },
  { id: 32, name: 'NY', fullName: 'New York' },
  { id: 33, name: 'NC', fullName: 'North Carolina' },
  { id: 34, name: 'ND', fullName: 'North Dakota' },
  { id: 35, name: 'OH', fullName: 'Ohio' },
  { id: 36, name: 'OK', fullName: 'Oklahoma' },
  { id: 37, name: 'OR', fullName: 'Oregon' },
  { id: 38, name: 'PA', fullName: 'Pennsylvania' },
  { id: 39, name: 'RI', fullName: 'Rhode Island' },
  { id: 40, name: 'SC', fullName: 'South Carolina' },
  { id: 41, name: 'SD', fullName: 'South Dakota' },
  { id: 42, name: 'TN', fullName: 'Tennessee' },
  { id: 43, name: 'TX', fullName: 'Texas' },
  { id: 44, name: 'UT', fullName: 'Utah' },
  { id: 45, name: 'VT', fullName: 'Vermont' },
  { id: 46, name: 'VA', fullName: 'Virginia' },
  { id: 47, name: 'WA', fullName: 'Washington' },
  { id: 48, name: 'WV', fullName: 'West Virginia' },
  { id: 49, name: 'WI', fullName: 'Wisconsin' },
  { id: 50, name: 'WY', fullName: 'Wyoming' },
  { id: 51, name: 'DC', fullName: 'District of Columbia' },
  { id: 52, name: 'PR', fullName: 'Puerto Rico' },
  { id: 53, name: 'OTHER', fullName: 'Other' },
  { id: 54, name: 'AB', fullName: 'Alberta' },
  { id: 55, name: 'BC', fullName: 'British Columbia' },
  { id: 56, name: 'MB', fullName: 'Manitoba' },
  { id: 57, name: 'NB', fullName: 'New Brunswick' },
  { id: 58, name: 'NL', fullName: 'Newfoundland and Labrador' },
  { id: 59, name: 'NT', fullName: 'Northwest Territories' },
  { id: 60, name: 'NS', fullName: 'Nova Scotia' },
  { id: 61, name: 'NU', fullName: 'Nunavut' },
  { id: 62, name: 'ON', fullName: 'Ontario' },
  { id: 63, name: 'PE', fullName: 'Prince Edward Island' },
  { id: 64, name: 'QC', fullName: 'Quebec' },
  { id: 65, name: 'SK', fullName: 'Saskatchewan' },
  { id: 66, name: 'YT', fullName: 'Yukon' },
  { id: 67, name: 'D', fullName: 'Diplomatic' },
  { id: 68, name: 'ML', fullName: 'Military' },
  { id: 69, name: 'G', fullName: 'Government' },
  { id: MX_PLATE_STATE_ID, name: 'MX', fullName: 'All States' },
];
